import type {Ability} from '../../../../generated/types';
import {isAbility} from './abilities';
import type {HitPointsState} from './character-state';
import {evaluateFormula, evaluateNumber, FormulaContext, resolveReference} from './formula';
import type {FormulaValue} from './types';

type SaveProps = {ability: string; dc: FormulaValue};

/** вариант эффекта, из которых выбирают при использовании: Божественная искра, Изгнание нежити */
export type ProcedureOptionProps = {
	title: string;
	/** кость эффекта: `1d8` */
	dice?: string;
	/** кость по уровню владельца: `{"7": "2d8"}` */
	diceByLevel?: Record<string, string>;
	/** прибавка к броску: `{Wis}` */
	bonus?: FormulaValue;
	/** бросок может лечить */
	healing?: boolean;
	/** бросок может наносить урон этих типов */
	damageTypes?: string[];
	save?: SaveProps;
};

/**
 * Что происходит по кнопке «Применить», если это больше, чем трата использования.
 * resource — ресурс, с которым работает процедура: Очки чар, запас Возложения рук.
 */
export type ProcedureProps =
	/** вернуть потраченные ячейки суммарно до levels кругов: Магическое восстановление */
	| {type: 'slotRecovery'; levels: FormulaValue; maxSlotLevel: number}
	/** вернуть очки ресурса: amount или все; healing — бросок лечения тем же применением */
	| {type: 'resourceRecovery'; resource: string; amount?: FormulaValue; healing?: FormulaValue}
	/** вернуть до половины ячеек договора: Магическая изворотливость */
	| {type: 'pactSlotRecovery'}
	/** заменить подготовленное заклинание: Запоминание заклинания */
	| {type: 'spellSwap'}
	/** заменить вариант в выборе особенности: Добыча охотника */
	| {type: 'changeChoice'}
	/** потратить часть запаса на лечение: Возложение рук */
	| {type: 'healingPool'; resource: string}
	/** ячейки в очки и очки в ячейки по таблице цен: Источник магии */
	| {
			type: 'slotConversion';
			resource: string;
			/** цена ячейки в очках по кругу */
			costs: Record<string, number>;
			/** минимальный уровень владельца для ячейки этого круга */
			minLevels: Record<string, number>;
			/** очками можно только вернуть потраченную ячейку, а не создать новую */
			restoreOnly?: boolean;
	  }
	/** ячейка в использование Дикого облика и использование в ячейку 1-го круга */
	| {type: 'wildResurgence'; resource: string}
	/** выбор одного из эффектов */
	| {type: 'options'; options: ProcedureOptionProps[]}
	/** сотворить заклинание из списка класса без ячейки: Божественное вмешательство */
	| {type: 'spellChoice'; spellList: string; maxLevel: number};

export type ProcedureOption = {
	title: string;
	/** формула броска с бонусом: `2d8 + 3` */
	formula?: string;
	healing: boolean;
	damageTypes: string[];
	save?: {ability: Ability; dc: number};
};

export type Procedure =
	| {type: 'slotRecovery'; levels: number; maxSlotLevel: number}
	| {type: 'resourceRecovery'; resource: string; amount?: number; healing?: string}
	| {type: 'pactSlotRecovery'}
	| {type: 'spellSwap'}
	| {type: 'changeChoice'}
	| {type: 'healingPool'; resource: string}
	| {
			type: 'slotConversion';
			resource: string;
			costs: Record<string, number>;
			minLevels: Record<string, number>;
			restoreOnly: boolean;
			/** уровень владельца: от него зависят доступные круги */
			ownerLevel: number;
	  }
	| {type: 'wildResurgence'; resource: string}
	| {type: 'options'; options: ProcedureOption[]}
	| {type: 'spellChoice'; spellList: string; maxLevel: number};

export const resourceKey = (resource: string) => `resource:${resource}`;

export function evaluateProcedure(props: ProcedureProps, context: FormulaContext): Procedure {
	switch (props.type) {
		case 'slotRecovery':
			return {...props, levels: evaluateNumber(props.levels, context) || 0};
		case 'resourceRecovery': {
			const healing = evaluateFormula(props.healing, context);
			return {
				type: props.type,
				resource: props.resource,
				amount: evaluateNumber(props.amount, context),
				healing: healing == null ? undefined : String(healing),
			};
		}
		case 'slotConversion':
			return {...props, restoreOnly: !!props.restoreOnly, ownerLevel: context.level};
		case 'options':
			return {type: props.type, options: props.options.map((option) => evaluateOption(option, context))};
		default:
			return props;
	}
}

function evaluateOption(option: ProcedureOptionProps, context: FormulaContext): ProcedureOption {
	const levelDice = Object.entries(option.diceByLevel || {})
		.filter(([level]) => Number(level) <= context.level)
		.sort(([first], [second]) => Number(second) - Number(first))[0]?.[1];
	const dice = levelDice || option.dice;
	const bonus = evaluateNumber(option.bonus, context);
	const ability = option.save && resolveReference(option.save.ability, context);
	const dc = option.save && evaluateNumber(option.save.dc, context);
	return {
		title: option.title,
		formula: dice ? (bonus ? `${dice} ${bonus > 0 ? '+' : '-'} ${Math.abs(bonus)}` : dice) : undefined,
		healing: !!option.healing,
		damageTypes: option.damageTypes || [],
		save: ability && isAbility(ability) && dc != null ? {ability, dc} : undefined,
	};
}

/** лечение не опускает потраченные ПЗ ниже нуля; временные ПЗ не меняются */
export const healedHitPoints = (hitPoints: HitPointsState, amount: number): HitPointsState => ({
	...hitPoints,
	spent: Math.max(0, hitPoints.spent - Math.max(0, amount)),
});
